import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LicenseOverrideType } from '../../../../shared/license-list/models/license-override-list.model';
import { LicenseService } from '../../../../shared/license-list/services/license.service';
import { NihiiHelper } from '../../../../shared/helpers/nihii.helper';
import { LocalSettingsService } from '../../../../../lib/services/local-settings.service';

interface NihiiOverrideOption {
  readonly value: Exclude<LicenseOverrideType, 'IN_ORDER'>;
  readonly label: string;
}

@Component({
  selector: 'ms-nihii-override-dialog',
  templateUrl: './nihii-override-dialog.component.html',
  styleUrls: ['./nihii-override-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NihiiOverrideDialogComponent {
  public readonly overrideTypes: NihiiOverrideOption[] = [
    { value: 'READ_ONLY', label: 'Lecture seule' },
    { value: 'GRACE_PERIOD', label: 'Avertissement' },
    { value: 'NOT_VALID', label: 'Bloqué' },
  ];

  public readonly form: FormGroup<{
    encodedNihii: FormControl<string>;
    overrideType: FormControl<Exclude<LicenseOverrideType, 'IN_ORDER'> | null>;
    reason: FormControl<string>;
  }> = new FormGroup({
    encodedNihii: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, this.validateNihii.bind(this)],
    }),
    overrideType: new FormControl<Exclude<
      LicenseOverrideType,
      'IN_ORDER'
    > | null>(null, {
      validators: [Validators.required],
    }),
    reason: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3)],
    }),
  });

  constructor(
    private dialogRef: MatDialogRef<NihiiOverrideDialogComponent>,
    private licenseService: LicenseService,
    private localSettingsService: LocalSettingsService,
  ) {}

  public get normalizedNihii(): string {
    return NihiiHelper.normalizeToNihii8(this.encodedNihiiControl.value);
  }

  public get encodedNihiiControl(): FormControl<string> {
    return this.form.controls.encodedNihii;
  }

  public get overrideTypeControl(): FormControl<Exclude<
    LicenseOverrideType,
    'IN_ORDER'
  > | null> {
    return this.form.controls.overrideType;
  }

  public get reasonControl(): FormControl<string> {
    return this.form.controls.reason;
  }

  public get canSubmit(): boolean {
    return this.form.valid;
  }

  public get hasNihiiError(): boolean {
    const control: FormControl<string> = this.encodedNihiiControl;
    return control.invalid && (control.dirty || control.touched);
  }

  public get hasOverrideTypeError(): boolean {
    const control = this.overrideTypeControl;
    return control.invalid && (control.dirty || control.touched);
  }

  public get hasReasonError(): boolean {
    const control: FormControl<string> = this.reasonControl;
    return control.invalid && (control.dirty || control.touched);
  }

  public closeDialog(): void {
    this.dialogRef.close();
  }

  public async addNihiiOverride(): Promise<void> {
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      return;
    }

    const token: string | null = this.localSettingsService.oauthIdToken;

    if (!token) {
      return;
    }

    await this.licenseService.createNihiiOverride(
      token,
      this.normalizedNihii,
      this.overrideTypeControl.value as Exclude<
        LicenseOverrideType,
        'IN_ORDER'
      >,
      this.reasonControl.value,
    );

    this.dialogRef.close({ created: true });
  }

  private validateNihii(
    control: AbstractControl<string | null>,
  ): ValidationErrors | null {
    const normalizedNihii: string = NihiiHelper.normalizeToNihii8(
      control.value ?? '',
    );

    if (!normalizedNihii) {
      return null;
    }

    if (normalizedNihii.length < 8) {
      return { nihiiLength: true };
    }

    if (!NihiiHelper.hasValidCheckDigit(normalizedNihii)) {
      return { nihiiCheckDigit: true };
    }

    return null;
  }

  public get isValid(): boolean {
    return this.form.valid;
  }
}
